import { useEffect, RefObject } from "react";

interface ScrollOptions {
  behavior?: ScrollBehavior;
  block?: ScrollLogicalPosition;
  offset?: number;
}

// Utility function for scrolling to an element
export function scrollToElement(
  element: HTMLElement | null,
  options: ScrollOptions = {},
) {
  if (!element) return;

  const { behavior = "smooth", block = "start", offset = 0 } = options;

  if (offset) {
    const elementRect = element.getBoundingClientRect();
    const absoluteElementTop = elementRect.top + window.pageYOffset;
    const offsetPosition = absoluteElementTop - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior,
    });
  } else {
    element.scrollIntoView({
      behavior,
      block,
    });
  }
}

// Scrolls the window to the top when the dependencies change.
//
// The effect body is a block, not a concise arrow: window.scrollTo returns a
// promise in current browsers, and React treats whatever an effect returns as
// its cleanup function.
export function useScrollToTop(dependencies: unknown[] = []) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, dependencies);
}

// Separate custom hook for scroll on transition
export function useScrollOnTransition(
  ref: RefObject<HTMLElement>,
  dependencies: any[],
  options: ScrollOptions = {},
  skipInitial: boolean = true,
) {
  useEffect(() => {
    // Skip initial scroll if requested
    if (skipInitial && dependencies.some((dep) => dep === 0 || dep === null)) {
      return;
    }

    if (ref.current) {
      scrollToElement(ref.current, options);
    }
  }, dependencies);
}
